import { z } from 'zod';
import { numericInteger, UploadedMediaSchema } from '@components/Dashboard/Upload/helpers';
import { MAX_CHAR_LENGTH } from '@components/Dashboard/Upload/validation';
import { LicenseType, MediaType, SongKeyType } from '@graphql/reactQuery';
import { ReleaseInfoErrors } from './errors';

export const TrackUploadSchema = z.object({
  coverImage: UploadedMediaSchema.nullish().default(null),
  duration: z.number(),
  fileDetail: UploadedMediaSchema,
  normalizedPeaks: z.array(z.number()).optional().default([]),
  title: z.string(),
});

export const MediaUploadSchema = z.object({
  mediaType: z.nativeEnum(MediaType),
  uploadKey: z.string(),
  cdnUrl: z.string(),
});

export type MediaUploadType = z.infer<typeof MediaUploadSchema>;

export const ReleaseInfoFields = z.object({
  title: z.string().nullable(),
  tokenSymbol: z.string().nullable(),
  track: TrackUploadSchema.nullable(),
  coverImage: MediaUploadSchema.nullable(),
  staticCoverImage: MediaUploadSchema.nullable(),
  editionCoverGa: MediaUploadSchema.nullable(),
  editionCoverGoldenEgg: MediaUploadSchema.nullable(),
  genre: z.string().nullable(),
  behindTheMusic: z.string().nullable(),
  beatsPerMinute: numericInteger.nullish().default(null),
  key: z.nativeEnum(SongKeyType).nullish().default(null),
  lyrics: z.string().trim().nullish().default(null),
  license: z.nativeEnum(LicenseType).nullish().default(null),
  location: z.string().nullish().default(null),
  canCollectorsDownloadAudio: z.boolean().nullish(),
  curatorRewardsEnabled: z.boolean().nullish(),
  rewards: z
    .array(
      z.object({
        title: z.string().trim(),
        description: z.string().max(MAX_CHAR_LENGTH.rewardDescription).trim(),
      }),
    )
    .nullish(),
});

export type ReleaseInfoFields = (typeof ReleaseInfoFields)['_input'];

export const ReleaseInfoTouchedFields = z.object({
  title: z.boolean(),
  tokenSymbol: z.boolean(),
  track: z.boolean(),
  coverImage: z.boolean(),
  staticCoverImage: z.boolean(),
  editionCoverGa: z.boolean(),
  editionCoverGoldenEgg: z.boolean(),
  genre: z.boolean(),
  behindTheMusic: z.boolean(),
  beatsPerMinute: z.boolean(),
  key: z.boolean(),
  lyrics: z.boolean(),
  license: z.boolean(),
  location: z.boolean(),
  canCollectorsDownloadAudio: z.boolean(),
  curatorRewardsEnabled: z.boolean(),
  rewards: z.object({ title: z.boolean(), description: z.boolean() }).array(),
});

const ReleaseInfoAccordionStateFields = z.object({
  value: z.string().nullable(),
});

export type ReleaseInfoTouchedFields = (typeof ReleaseInfoTouchedFields)['_input'];

export const ReleaseInfoSchema = z.object({
  fields: ReleaseInfoFields,
  touched: ReleaseInfoTouchedFields,
  accordion: ReleaseInfoAccordionStateFields,
  errors: ReleaseInfoErrors,
  isValid: z.boolean(),
});

export type ReleaseInfoState = (typeof ReleaseInfoSchema)['_input'];
